import type {
  BandwidthReport,
  HostDeliveryMode,
  NetworkTransfer,
  ThroughputBucket,
  TransferCategory,
} from "@/diagnostics/networkBandwidth/types";
import { DEFAULT_BANDWIDTH_MONITOR_CONFIG } from "@/diagnostics/networkBandwidth/types";

const BYTES_TO_Mbps = (bytes: number, durationMs: number): number => {
  if (durationMs <= 0 || bytes <= 0) return 0;
  return (bytes * 8) / (durationMs / 1000) / 1_000_000;
};

const BYTES_TO_Kbps = (bytes: number, durationMs: number): number => {
  if (durationMs <= 0 || bytes <= 0) return 0;
  return (bytes * 8) / (durationMs / 1000) / 1_000;
};

/** True when the hostname is a Medplum API endpoint. */
export const isMedplumApiHost = (hostname: string): boolean =>
  hostname === "medplum.com" || hostname.endsWith(".medplum.com");

/** Classify a transfer for filtering and API vs asset breakdowns. */
export const classifyTransferUrl = (url: string): TransferCategory => {
  try {
    const parsed = new URL(url, window.location.origin);
    if (
      isMedplumApiHost(parsed.hostname) ||
      parsed.pathname.startsWith("/api/") ||
      parsed.pathname.includes("/fhir/")
    ) {
      return "api";
    }
    if (
      parsed.origin === window.location.origin &&
      (/\.(js|css|woff2?|png|svg|ico|map)$/i.test(parsed.pathname) ||
        parsed.pathname.startsWith("/assets/"))
    ) {
      return "asset";
    }
  } catch {
    // fall through
  }
  return "other";
};

export const detectHostDeliveryMode = (): HostDeliveryMode =>
  import.meta.env.DEV ? "vite-dev" : "static-hosting";

/** Build fixed-width throughput buckets from transfer events. */
export const buildThroughputBuckets = (
  transfers: readonly NetworkTransfer[],
  sessionStartMs: number,
  sessionEndMs: number,
  bucketWidthMs = DEFAULT_BANDWIDTH_MONITOR_CONFIG.bucketWidthMs,
): ThroughputBucket[] => {
  if (sessionEndMs <= sessionStartMs) return [];

  const bucketCount = Math.ceil(
    (sessionEndMs - sessionStartMs) / bucketWidthMs,
  );
  const buckets: ThroughputBucket[] = Array.from(
    { length: bucketCount },
    (_, i) => ({
      startTimeMs: sessionStartMs + i * bucketWidthMs,
      downloadBytes: 0,
      uploadBytes: 0,
      downloadBps: 0,
      uploadBps: 0,
    }),
  );

  for (const transfer of transfers) {
    const startIdx = Math.max(
      0,
      Math.floor((transfer.startTimeMs - sessionStartMs) / bucketWidthMs),
    );
    const endIdx = Math.min(
      bucketCount - 1,
      Math.floor((transfer.endTimeMs - sessionStartMs) / bucketWidthMs),
    );
    const spanBuckets = Math.max(1, endIdx - startIdx + 1);
    const bytesPerBucket = transfer.bytes / spanBuckets;

    for (let i = startIdx; i <= endIdx; i += 1) {
      const bucket = buckets[i];
      if (!bucket) continue;
      if (transfer.direction === "download") {
        bucket.downloadBytes += bytesPerBucket;
      } else {
        bucket.uploadBytes += bytesPerBucket;
      }
    }
  }

  const seconds = bucketWidthMs / 1000;
  for (const bucket of buckets) {
    bucket.downloadBps = bucket.downloadBytes / seconds;
    bucket.uploadBps = bucket.uploadBytes / seconds;
  }

  return buckets;
};

/** Sum bytes in a rolling window of buckets aligned to `windowMs`. */
export const rollingWindowRates = (
  buckets: readonly ThroughputBucket[],
  bucketWidthMs: number,
  windowMs: number,
): Array<{ startTimeMs: number; downloadBps: number; uploadBps: number }> => {
  const windowBuckets = Math.max(1, Math.round(windowMs / bucketWidthMs));
  const results: Array<{
    startTimeMs: number;
    downloadBps: number;
    uploadBps: number;
  }> = [];

  for (let i = 0; i < buckets.length; i += 1) {
    const slice = buckets.slice(i, i + windowBuckets);
    const downloadBytes = slice.reduce((sum, b) => sum + b.downloadBytes, 0);
    const uploadBytes = slice.reduce((sum, b) => sum + b.uploadBytes, 0);
    const durationMs = slice.length * bucketWidthMs;
    results.push({
      startTimeMs: buckets[i]?.startTimeMs ?? 0,
      downloadBps: downloadBytes / (durationMs / 1000),
      uploadBps: uploadBytes / (durationMs / 1000),
    });
  }

  return results;
};

export type AggregatorInput = {
  sessionStartMs: number;
  nowMs: number;
  transfers: readonly NetworkTransfer[];
  bucketWidthMs?: number;
  rollingWindowMs?: number;
  idleThresholdBytes?: number;
  connectionDownlinkMbps?: number | null;
  connectionRttMs?: number | null;
  connectionEffectiveType?: string | null;
  hostDeliveryMode?: HostDeliveryMode;
  buildMode?: string;
  includeTimeline?: boolean;
};

/** Compute a full bandwidth report from raw transfers. */
export const aggregateBandwidthReport = ({
  sessionStartMs,
  nowMs,
  transfers,
  bucketWidthMs = DEFAULT_BANDWIDTH_MONITOR_CONFIG.bucketWidthMs,
  rollingWindowMs = DEFAULT_BANDWIDTH_MONITOR_CONFIG.rollingWindowMs,
  idleThresholdBytes = DEFAULT_BANDWIDTH_MONITOR_CONFIG.idleThresholdBytes,
  connectionDownlinkMbps = null,
  connectionRttMs = null,
  connectionEffectiveType = null,
  hostDeliveryMode = detectHostDeliveryMode(),
  buildMode = import.meta.env.MODE,
  includeTimeline = true,
}: AggregatorInput): BandwidthReport => {
  const sessionDurationMs = Math.max(0, nowMs - sessionStartMs);

  const downloads = transfers.filter((t) => t.direction === "download");
  const uploads = transfers.filter((t) => t.direction === "upload");

  const totalBytesDownloaded = downloads.reduce((sum, t) => sum + t.bytes, 0);
  const totalBytesUploaded = uploads.reduce((sum, t) => sum + t.bytes, 0);

  const apiBytesDownloaded = downloads
    .filter((t) => t.category === "api")
    .reduce((sum, t) => sum + t.bytes, 0);
  const apiBytesUploaded = uploads
    .filter((t) => t.category === "api")
    .reduce((sum, t) => sum + t.bytes, 0);
  const assetBytesDownloaded = downloads
    .filter((t) => t.category === "asset")
    .reduce((sum, t) => sum + t.bytes, 0);

  const largestDownload =
    downloads.length > 0
      ? downloads.reduce((max, t) => (t.bytes > max.bytes ? t : max))
      : null;
  const largestUpload =
    uploads.length > 0
      ? uploads.reduce((max, t) => (t.bytes > max.bytes ? t : max))
      : null;

  const throughputTimeline = buildThroughputBuckets(
    transfers,
    sessionStartMs,
    nowMs,
    bucketWidthMs,
  );

  const windows = rollingWindowRates(
    throughputTimeline,
    bucketWidthMs,
    rollingWindowMs,
  );

  let peakDownloadBps = 0;
  let peakUploadBps = 0;
  let peakDownloadAtMs: number | null = null;
  let peakUploadAtMs: number | null = null;

  const activeDownloadRates: number[] = [];
  const activeUploadRates: number[] = [];
  const idleDownloadRates: number[] = [];

  for (const window of windows) {
    const windowDownloadBytes = window.downloadBps * (rollingWindowMs / 1000);
    const windowUploadBytes = window.uploadBps * (rollingWindowMs / 1000);

    if (window.downloadBps > peakDownloadBps) {
      peakDownloadBps = window.downloadBps;
      peakDownloadAtMs = window.startTimeMs;
    }
    if (window.uploadBps > peakUploadBps) {
      peakUploadBps = window.uploadBps;
      peakUploadAtMs = window.startTimeMs;
    }

    if (windowDownloadBytes >= idleThresholdBytes) {
      activeDownloadRates.push(window.downloadBps);
    } else {
      idleDownloadRates.push(window.downloadBps);
    }

    if (windowUploadBytes >= idleThresholdBytes) {
      activeUploadRates.push(window.uploadBps);
    }
  }

  const minSustainedDownloadBps =
    activeDownloadRates.length > 0 ? Math.min(...activeDownloadRates) : 0;
  const minSustainedUploadBps =
    activeUploadRates.length > 0 ? Math.min(...activeUploadRates) : 0;

  const sortedIdle = [...idleDownloadRates].sort((a, b) => a - b);
  const idleMedian =
    sortedIdle.length > 0
      ? (sortedIdle[Math.floor(sortedIdle.length / 2)] ?? 0)
      : 0;

  return {
    sessionStartMs,
    sessionDurationMs,
    totalBytesDownloaded,
    totalBytesUploaded,
    transferCount: transfers.length,
    hostDeliveryMode,
    buildMode,
    peakDownloadMbps: BYTES_TO_Mbps(peakDownloadBps, 1000),
    peakUploadMbps: BYTES_TO_Mbps(peakUploadBps, 1000),
    peakDownloadAtMs,
    peakUploadAtMs,
    minSustainedDownloadMbps: BYTES_TO_Mbps(minSustainedDownloadBps, 1000),
    minSustainedUploadMbps: BYTES_TO_Mbps(minSustainedUploadBps, 1000),
    idleDownloadKbps: BYTES_TO_Kbps(idleMedian, 1000),
    averageDownloadMbps: BYTES_TO_Mbps(totalBytesDownloaded, sessionDurationMs),
    averageUploadMbps: BYTES_TO_Mbps(totalBytesUploaded, sessionDurationMs),
    apiBytesDownloaded,
    apiBytesUploaded,
    assetBytesDownloaded,
    largestDownload,
    largestUpload,
    connectionDownlinkMbps: connectionDownlinkMbps ?? null,
    connectionRttMs: connectionRttMs ?? null,
    connectionEffectiveType: connectionEffectiveType ?? null,
    recentTransfers: [...transfers].slice(-20).reverse(),
    throughputTimeline: includeTimeline ? throughputTimeline : [],
  };
};

/** Human-readable summary for console logging and clipboard export. */
export const formatBandwidthSummary = (report: BandwidthReport): string => {
  const lines = [
    `[bandwidth] ${report.buildMode} / ${report.hostDeliveryMode} — ${(report.sessionDurationMs / 1000).toFixed(1)}s`,
    `  peak download:     ${report.peakDownloadMbps.toFixed(2)} Mbps`,
    `  peak upload:       ${report.peakUploadMbps.toFixed(2)} Mbps`,
    `  min sustained dl:  ${report.minSustainedDownloadMbps.toFixed(2)} Mbps (active windows)`,
    `  min sustained ul:  ${report.minSustainedUploadMbps.toFixed(2)} Mbps (active windows)`,
    `  API traffic:       ${(report.apiBytesDownloaded / 1024).toFixed(1)} KB down / ${(report.apiBytesUploaded / 1024).toFixed(1)} KB up`,
    `  static assets:     ${(report.assetBytesDownloaded / 1024).toFixed(1)} KB down`,
    `  total transferred: ${(report.totalBytesDownloaded / 1024).toFixed(1)} KB down / ${(report.totalBytesUploaded / 1024).toFixed(1)} KB up`,
    `  transfers:         ${report.transferCount}`,
  ];
  if (report.connectionDownlinkMbps !== null) {
    lines.push(
      `  browser estimate:  ${report.connectionDownlinkMbps.toFixed(2)} Mbps down, RTT ${report.connectionRttMs ?? "?"} ms (${report.connectionEffectiveType ?? "unknown"})`,
    );
  }
  return lines.join("\n");
};

/** Export payload with timeline omitted to keep downloads small. */
export const toBandwidthExport = (
  report: BandwidthReport,
  scenarioMarks: ReadonlyArray<{
    name: string;
    atMs: number;
    report: BandwidthReport;
  }>,
): Record<string, unknown> => {
  const { throughputTimeline: _timeline, ...summaryReport } = report;
  return {
    exportedAt: new Date().toISOString(),
    summary: formatBandwidthSummary(report),
    report: summaryReport,
    scenarioMarks: scenarioMarks.map((mark) => ({
      name: mark.name,
      atMs: mark.atMs,
      peakDownloadMbps: mark.report.peakDownloadMbps,
      peakUploadMbps: mark.report.peakUploadMbps,
      minSustainedDownloadMbps: mark.report.minSustainedDownloadMbps,
      transferCount: mark.report.transferCount,
      apiBytesDownloaded: mark.report.apiBytesDownloaded,
      assetBytesDownloaded: mark.report.assetBytesDownloaded,
    })),
  };
};
